import React, { useState, useEffect } from 'react'
import { Box, Alert, CircularProgress, Button, Typography } from '@mui/material'
import clsx from 'clsx'
import QRCode from 'react-qr-code'

const API_BASE_URL = 'https://api.neontradepro.com'

interface UserInfo {
  id: number
  first_name: string
  last_name: string
  email: string
  phone: string
  username: string
  telegram_username: string
  referral_code: string
  referrer_code: string
  wallet: number
  telegram_invite_url?: string
  telegram_bot_connected?: boolean
}

interface ItemProps {
  label: string
  value: string
  editable?: boolean
  onSubmit?: (new_value: string) => Promise<boolean>
}

const Item: React.FC<ItemProps> = ({ label, value, editable = false, onSubmit }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [valueState, setValueState] = useState(value)
  const theme = localStorage.getItem('theme')

  const handleToggleEdit = async () => {
    if (isEditing && onSubmit) {
      const result = await onSubmit(valueState)
      if (!result) setValueState(value)
    }
    setIsEditing(!isEditing)
  }

  return (
    <div
      className={clsx(
        'p-3 mb-2 rounded-md flex items-center justify-between',
        theme === 'dark' ? 'bg-white/10' : 'bg-black/10'
      )}
    >
      <div className='flex-1'>
        <div className='text-sm text-gray-500 mb-1'>{label}</div>
        <div className='flex'>
          {isEditing ? (
            <input
              type='text'
              value={valueState}
              onChange={e => setValueState(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') handleToggleEdit()
              }}
              className='text-sm w-full rounded bg-white text-black'
            />
          ) : (
            <div className='text-sm w-full'>{valueState}</div>
          )}
          {editable && (
            <button onClick={handleToggleEdit} className='mr-2 text-sm hover:scale-110 transition flex items-center'>
              {isEditing ? (
                <span className='material-icons text-base'>check</span>
              ) : (
                <span className='material-icons text-base'>edit</span>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function AccountProfile() {
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'warning' | 'error' | 'info'>('info')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [userData, setUserData] = useState<UserInfo>(null)
  const theme = localStorage.getItem('theme')

  useEffect(() => {
    const fetchProfileData = async () => {
      setLoading(true)
      try {
        const response = await fetch(`${API_BASE_URL}/user/info`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        if (!response.ok) throw new Error('خطا در دریافت اطلاعات پروفایل')
        const data = await response.json()
        setUserData(data)
      } catch (err) {
        setError('خطا در بارگذاری اطلاعات. لطفاً بعداً دوباره تلاش کنید.')
      } finally {
        setLoading(false)
      }
    }
    fetchProfileData()
  }, [])

  const newOnSubmit = (key: string) => {
    const onSubmit = async (new_value: string): Promise<boolean> => {
      const token = localStorage.getItem('token')
      if (!token) {
        setShowAlert(true)
        setAlertMessage('توکن احراز هویت یافت نشد. لطفاً وارد شوید.')
        setAlertSeverity('error')
        setLoading(false)
        return false
      }

      try {
        const response = await fetch(`${API_BASE_URL}/user/settings`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ [key]: new_value })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.detail || 'خطا در دریافت کاربران دعوت شده')
        }

        setShowAlert(true)
        setAlertMessage('اطلاعات با موفقیت ویرایش شد.')
        setAlertSeverity('success')
        setUserData({ ...userData, [key]: new_value })
        return true
      } catch (err: any) {
        setShowAlert(true)
        setAlertMessage(err.message)
        setAlertSeverity('error')
        return false
      }
    }
    return onSubmit
  }

  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    )
  if (error && !userData) return <Alert severity='error'>{error}</Alert>

  return (
    <div>
      {showAlert && (
        <Alert severity={alertSeverity} sx={{ mb: 2, width: '100%', '.MuiAlert-message': { width: '100%' } }}>
          {alertMessage}
        </Alert>
      )}

      <div className='grid grid-cols-2 gap-1'>
        <Item label='نام' value={userData?.first_name} editable onSubmit={newOnSubmit('first_name')} />
        <Item label='نام خانوادگی' value={userData?.last_name} editable onSubmit={newOnSubmit('last_name')} />
      </div>

      <div className='grid grid-cols-2 gap-1'>
        <Item label='نام‌کاربری' value={userData?.username} editable onSubmit={newOnSubmit('username')} />
        <Item label='ایمیل' value={userData?.email} />
      </div>

      <Item label='شماره تلفن' value={userData?.phone} />

      <div className={clsx('p-3 mb-2 rounded-md', theme === 'dark' ? 'bg-white/10' : 'bg-black/10')}>
        <div className='text-sm text-gray-500 mb-2'>وضعیت اتصال به ربات تلگرام</div>

        {userData?.telegram_bot_connected ? (
          <Alert severity='success' icon={<span className='material-icons'>check_circle</span>}>
            حساب شما به ربات تلگرام متصل است.
            {userData?.telegram_username && !Number(userData.telegram_username) && (
              <div>آیدی: @{userData.telegram_username}</div>
            )}
          </Alert>
        ) : (
          <div className='flex flex-col md:flex-row items-center justify-between gap-4 p-2'>
            <div className='flex flex-col gap-2 flex-1'>
              <Typography variant='body2' sx={{ color: 'var(--text-color)' }}>
                برای دریافت نوتیفیکیشن‌ها و مدیریت حساب، لطفاً با استفاده از دکمه یا اسکن کد زیر، ربات تلگرام را استارت
                کنید.
              </Typography>
              {userData?.telegram_invite_url && (
                <Button
                  variant='contained'
                  color='primary'
                  href={userData.telegram_invite_url}
                  target='_blank'
                  sx={{ alignSelf: 'start', mt: 1 }}
                >
                  <span className='material-icons ml-2'>telegram</span>
                  اتصال به ربات تلگرام
                </Button>
              )}
            </div>

            {userData?.telegram_invite_url && (
              <div className='bg-white p-2 rounded-lg shrink-0'>
                <QRCode
                  size={120}
                  style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                  value={userData.telegram_invite_url}
                  viewBox={`0 0 120 120`}
                />
              </div>
            )}
          </div>
        )}
      </div>

      <Item
        label={userData?.referrer_code === null ? 'ثبت معرف' : 'دعوت شده از طرف'}
        value={userData?.referrer_code}
        editable={userData?.referrer_code === null}
        onSubmit={newOnSubmit('referrer_code')}
      />
    </div>
  )
}
export default AccountProfile
